<template>
  <h3 class="source-title">{{ featureSource }} metrics</h3>
  <keep-alive v-if="!isLoading && data?.length === 0">
    <div v-if="searchTerm" class="error-message">
      <p>Your search for a feature has no results</p>
    </div>
    <div v-else class="error-message">
      <VdIcon store="fa" name="info-circle" color="var(--vd-color-blue-default)" />
      <p v-if="isWip">{{ errorMessage }}</p>
      <p v-else>Sorry, there is no data.</p>
    </div>
  </keep-alive>
  <el-table v-else :data="data" style="width: 100%" class="features-table">
    <el-table-column prop="name" sortable label="Name" width="330">
      <template #default="scope">
        <div class="name">
          <h4 class="name-link" data-cy="feature" @click="goToPreviewFeature(scope.row.id, scope.row.source)">
            {{ scope.row.name || "(unnamed)" }}
          </h4>
          <h5 class="technical-name">{{ scope.row.id }}</h5>
        </div>
      </template>
    </el-table-column>
    <el-table-column v-if="featureSource === 'custom'" prop="source" label="Type">
      <template #default="scope">
        <div class="feature-badge source-type">{{ sourcesMap[scope.row.source] }}</div>
      </template>
    </el-table-column>
    <el-table-column prop="category" label="Category">
      <template #default="scope">
        <div class="feature-badge">{{ scope.row.category.replaceAll("_", " ") }}</div>
      </template>
    </el-table-column>
    <el-table-column prop="description" label="Description" />
    <el-table-column prop="displayType" sortable label="Data type" width="180" />
    <el-table-column v-if="isWip && canWriteFeatures" fixed="right" label="" width="60">
      <template #default="scope">
        <vd-dropdown v-if="showExtraActions">
          <vd-dropdown-item @click="onFeatureEdit(scope.row.id, scope.row.source)">Edit</vd-dropdown-item>
          <vd-dropdown-item
            v-if="scope.row.source === 'custom'"
            dangerous
            @click="emits('on-feature-delete', scope.row.id)"
          >
            Delete
          </vd-dropdown-item>
        </vd-dropdown>
      </template>
    </el-table-column>
  </el-table>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";

import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

const emits = defineEmits(["on-feature-delete"]);

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  isWip: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  showExtraActions: {
    type: Boolean,
    required: false,
    default: true,
  },
  searchTerm: {
    type: String,
    required: true,
  },
  data: {
    type: Array,
  },
  featureSource: {
    type: String,
    required: true,
  },
});

const sourcesMap: { [key: string]: string } = {
  app_custom: "VoodooSauce",
  payment_custom: "Payment",
};

const router = useRouter();
const { canWriteFeatures } = usePermissions();

const errorMessageMap: Map<string, string> = new Map([
  ["custom", 'No custom metrics found. To create one, click on "Link new metric"'],
  ["system", "No system metrics found. Refresh the page or contact the support team"],
  ["external", "No external metrics found. Refresh the page or contact the support team"],
]);

const errorMessage = errorMessageMap.get(props.featureSource) ?? errorMessageMap.get("custom");

const goToPreviewFeature = (featureId: string, source: string) => {
  const name = source === "payment_custom" ? links.games.features.previewPayment : links.games.features.preview;
  router.push({ name, params: { featureId } });
};

const onFeatureEdit = (featureId: string, source: string) => {
  const name = source === "payment_custom" ? links.games.features.editPayment : links.games.features.edit;

  router.push({
    name,
    params: { gameId: props.gameId, versionId: props.versionId, featureId },
  });
};
</script>

<style lang="scss" scoped>
.source-title {
  font-weight: bold;
  margin-bottom: size("M");

  &:first-letter {
    text-transform: uppercase;
  }
}

.error-message {
  background-color: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-default);
  display: flex;
  align-items: center;
  width: fit-content;
  padding: 1rem;
  margin-bottom: 1rem;

  svg {
    margin-right: 0.75rem;
  }

  p {
    margin: 0;
  }
}

.features-table {
  @extend %card;
  margin-bottom: size("L");

  .feature-badge {
    font-size: size("M");
    padding: 0 8px;
    background-color: var(--vd-color-blue-light-9);
    color: var(--vd-color-blue-default);
    border-radius: 4px;
    width: fit-content;
    font-weight: 700;
    white-space: nowrap;

    &:first-letter {
      text-transform: uppercase;
    }

    &.source-type {
      background-color: var(--vd-color-pink-100);
      color: var(--vd-color-pink-600);
    }
  }

  .name {
    padding: size("ML") size("M");

    .name-link {
      color: var(--vd-base-primary-color);
      cursor: pointer;
      margin-bottom: size("M");
    }
    .technical-name {
      @extend %technical-name;
    }
  }
}
</style>
